import {i18n} from '@/lang/lang'
import {HmacMD5} from '@/app-core/lib/crypto/crypto'

import Home from '@/app-theme/views/home/Home.vue'
import Tag from '@/app-theme/views/tag/Tag.vue'
import Store from '@/app-theme/views/store/Store.vue'
import Category from '@/app-theme/views/category/Category.vue'
import CheckoutDetails from '@/app-theme/views/checkout/CheckoutDetails.vue'
import CheckoutSuccess from '@/app-theme/views/checkout/CheckoutSuccess.vue'
import CheckoutFailure from '@/app-theme/views/checkout/CheckoutFailure.vue'
import MarketplaceAccountConnected from '@/app-theme/views/marketplace/MarketplaceAccountConnected.vue'
import Pages from '@/app-theme/views/pages/Pages.vue'
import PageContent from '@/app-theme/views/pages/PageContent.vue'
import PageContact from '@/app-theme/views/pages/PageContact.vue'
import Account from '@/app-theme/views/account/Account.vue'
import AccountPersonalInfo from '@/app-theme/views/account/AccountPersonalInfo.vue'
import AccountAddresses from '@/app-theme/views/account/AccountAddresses.vue'
import AccountOrderHistory from '@/app-theme/views/account/AccountOrderHistory.vue'
import AccountSettings from '@/app-theme/views/account/AccountSettings.vue'
import MaintenanceMode from '@/app-theme/views/maintenance/MaintenanceMode.vue'

export const CoreRoutes = {
  SimpleRoutes: [
    {
      name    : 'Default',
      path    : '*',
      redirect: () => {
        return '/' + i18n.locale
      }
    },
    {
      name    : 'VivaPaymentSuccess',
      path    : '/payment/viva/success',
      redirect: to => {
        const [vivaLocale] = String(to.query.lang).toLowerCase().split('-')
        const language = i18n.languages.find(lang => lang.locale === vivaLocale)
        const storageKey = HmacMD5.hash('ORDERID')
        const item = sessionStorage.getItem(`${import.meta.env.VITE_STORAGE_PREFIX}_${storageKey}`) || ''
        const orderId = item ? JSON.parse(item) : '0'
        const paymentOrderCode = to.query.s || ''
        const paymentEventId = to.query.eventId || ''
        const payload = {
          name  : 'CheckoutSuccess',
          params: {
            lang            : language?.locale ?? import.meta.env.VITE_I18N_LOCALE,
            paymentOrderCode: paymentOrderCode,
            paymentEventId  : paymentEventId,
            orderId         : orderId,
            redirect        : true
          },
          query: {}
        }

        return payload
      },
      meta: {
        requiresAuth: false
      }
    },
    {
      name    : 'VivaPaymentFailure',
      path    : '/payment/viva/failure',
      redirect: to => {
        const [vivaLocale] = String(to.query.lang).toLowerCase().split('-')
        const language = i18n.languages.find(lang => lang.locale === vivaLocale)
        const storageKey = HmacMD5.hash('ORDERID')
        const item = sessionStorage.getItem(`${import.meta.env.VITE_STORAGE_PREFIX}_${storageKey}`) || ''
        const orderId = item ? JSON.parse(item) : '0'
        const paymentOrderCode = to.query.s || ''
        const paymentEventId = to.query.eventId || ''
        const payload = {
          name  : 'CheckoutFailure',
          params: {
            lang            : language?.locale ?? import.meta.env.VITE_I18N_LOCALE,
            paymentOrderCode: paymentOrderCode,
            paymentEventId  : paymentEventId,
            orderId         : orderId,
            redirect        : true,
            error           : true
          },
          query: {}
        }

        return payload
      },
      meta: {
        requiresAuth: false
      }
    },
    {
      name    : 'VivaMarketplaceAccountConnected',
      path    : '/marketplace/viva/account/connected',
      redirect: to => {
        const language = i18n.languages.find(lang => lang.code === to.query.lang)

        return {
          name  : 'MarketplaceAccountConnected',
          params: {
            lang    : language?.locale ?? import.meta.env.VITE_I18N_LOCALE,
            redirect: true
          },
          query: {}
        }
      },
      meta: {
        requiresAuth: false
      }
    }
  ],

  LangRoutes: [
    {
      name     : 'Home',
      path     : '',
      component: Home,
      meta     : {
        requiresAuth: false,
        layout      : 'default'
      }
    },
    {
      name     : 'MaintenanceMode',
      path     : '',
      component: MaintenanceMode,
      meta     : {
        requiresAuth: false,
        layout      : 'simple'
      }
    },
    {
      name     : 'Tag',
      path     : 'tag/:slug?/:id?',
      component: Tag,
      props    : true,
      meta     : {
        requiresAuth: false,
        layout      : 'default'
      }
    },
    {
      name     : 'Store',
      path     : 'store/:slug?/:id?',
      component: Store,
      props    : true,
      meta     : {
        requiresAuth: false,
        layout      : 'default'
      }
    },
    {
      name     : 'Category',
      path     : 'category/:slug?/:id?',
      component: Category,
      props    : true,
      meta     : {
        requiresAuth: false,
        layout      : 'default'
      }
    },
    {
      name     : 'CheckoutDetails',
      path     : 'checkout/details',
      component: CheckoutDetails,
      meta     : {
        requiresAuth: false,
        layout      : 'default'
      }
    },
    {
      name     : 'CheckoutSuccess',
      path     : 'checkout/success/:orderId?',
      component: CheckoutSuccess,
      props    : true,
      meta     : {
        requiresAuth: false,
        layout      : 'default'
      }
    },
    {
      name     : 'CheckoutFailure',
      path     : 'checkout/failure/:orderId?',
      component: CheckoutFailure,
      props    : true,
      meta     : {
        requiresAuth: false,
        layout      : 'default'
      }
    },
    {
      name     : 'OrderStatus',
      path     : 'order/status/:orderId?',
      component: CheckoutSuccess,
      props    : true,
      meta     : {
        requiresAuth: false,
        layout      : 'default'
      }
    },
    {
      name     : 'MarketplaceAccountConnected',
      path     : 'marketplace/viva/account/connected',
      component: MarketplaceAccountConnected,
      props    : true,
      meta     : {
        requiresAuth: false,
        layout      : 'simple'
      }
    },
    {
      name     : 'Account',
      path     : 'account',
      component: Account,
      redirect : {name: 'AccountPersonalInfo'},
      meta     : {
        requiresAuth: true,
        layout      : 'default'
      },
      children: [
        {
          name     : 'AccountPersonalInfo',
          path     : 'personal-info',
          component: AccountPersonalInfo,
          meta     : {
            requiresAuth: true,
            layout      : 'default'
          }
        },
        {
          name     : 'AccountAddresses',
          path     : 'addresses',
          component: AccountAddresses,
          meta     : {
            requiresAuth: true,
            layout      : 'default'
          }
        },
        {
          name     : 'AccountOrderHistory',
          path     : 'order-history',
          component: AccountOrderHistory,
          meta     : {
            requiresAuth: true,
            layout      : 'default'
          }
        },
        {
          name     : 'AccountSettings',
          path     : 'settings',
          component: AccountSettings,
          meta     : {
            requiresAuth: true,
            layout      : 'default'
          }
        }
      ]
    },
    {
      name     : 'Pages',
      path     : 'page',
      component: Pages,
      redirect : {name: 'PageAbout'},
      meta     : {
        requiresAuth: false
      },
      children: [
        {
          name     : 'PageContact',
          path     : 'contact',
          component: PageContact,
          meta     : {
            group       : 'about',
            requiresAuth: false
          }
        },
        {
          name     : 'PageAbout',
          path     : 'about',
          component: PageContent,
          meta     : {
            group       : 'about',
            requiresAuth: false
          }
        },
        {
          name     : 'PageHowItWorks',
          path     : 'how-it-works',
          component: PageContent,
          meta     : {
            group       : 'about',
            requiresAuth: false
          }
        },
        {
          name     : 'PageFaq',
          path     : 'faq',
          component: PageContent,
          meta     : {
            group       : 'about',
            requiresAuth: false
          }
        },
        {
          name     : 'PoliciesTerms',
          path     : 'policies/terms',
          component: PageContent,
          meta     : {
            group       : 'policies',
            requiresAuth: false
          }
        },
        {
          name     : 'PoliciesPrivacy',
          path     : 'policies/privacy',
          component: PageContent,
          meta     : {
            group       : 'policies',
            requiresAuth: false
          }
        },
        {
          name     : 'PoliciesCopyright',
          path     : 'policies/copyright',
          component: PageContent,
          meta     : {
            group       : 'policies',
            requiresAuth: false
          }
        },
        {
          name     : 'PoliciesCookies',
          path     : 'policies/cookies',
          component: PageContent,
          meta     : {
            group       : 'policies',
            requiresAuth: false
          }
        },
        {
          name     : 'PoliciesPayments',
          path     : 'policies/payments',
          component: PageContent,
          meta     : {
            group       : 'policies',
            requiresAuth: false
          }
        },
        {
          name     : 'PoliciesReturns',
          path     : 'policies/returns',
          component: PageContent,
          meta     : {
            group       : 'policies',
            requiresAuth: false
          }
        },
        {
          name     : 'PoliciesDsa',
          path     : 'policies/dsa',
          component: PageContent,
          meta     : {
            group       : 'policies',
            requiresAuth: false
          }
        },
        {
          name     : 'PoliciesAllergens',
          path     : 'misc/allergens',
          component: PageContent,
          meta     : {
            group       : 'policies',
            requiresAuth: false
          }
        }
      ]
    }
  ]
}

export default CoreRoutes
